import { AxiosInstance } from 'axios';
import { getScimConfig } from '../utils/envUtils';
import { ScimAuthService } from './ScimAuthService';
import { PhotoResponse } from '../types/ScimUser';
import { getAxiosInstance } from './axiosInstance';
import { debugLog, debugVerboseLog } from '../utils/debugLog';

export class ScimPhotoService {
    private api: AxiosInstance;
    private baseUrl: string;

    // Photo cache
    private photoCache: Map<string, PhotoResponse> = new Map();

    // Static photo cache that persists across all instances
    private static globalPhotoCache: Map<string, PhotoResponse> = new Map();

    constructor() {
        const scimConfig = getScimConfig();
        this.baseUrl = scimConfig.apiUrl;
        this.api = getAxiosInstance(this.baseUrl);
    }

    /**
     * Fetch user photo by ID
     * @param id - The user ID to fetch photo for
     * @returns Promise with photo data or empty object if no photo found
     */
    async getUserPhoto(id: string): Promise<PhotoResponse> {
        try {
            // Check cache first
            if (this.photoCache.has(id)) {
                debugVerboseLog(`📸 Photo cache hit for user: ${id}`);
                return this.photoCache.get(id)!;
            }

            if (ScimPhotoService.globalPhotoCache.has(id)) {
                debugVerboseLog(`📸 Global photo cache hit for user: ${id}`);
                const photo = ScimPhotoService.globalPhotoCache.get(id)!;
                this.photoCache.set(id, photo); // Add to instance cache
                return photo;
            }

            debugVerboseLog(`🌐 ScimPhotoService: Fetching photo for user: ${id} from /photo?id=${id}`);

            const apiResponse = await this.api.get(`/photo?id=${id}`);

            debugVerboseLog(`📊 ScimPhotoService: Received photo response for user: ${id}`, apiResponse.data);

            const photoData: PhotoResponse = apiResponse.data || {};

            // Cache the photo data
            this.photoCache.set(id, photoData);
            ScimPhotoService.globalPhotoCache.set(id, photoData);

            debugVerboseLog(`💾 Photo cached for user: ${id}`);
            return photoData;
        } catch (error) {
            console.error('❌ Error fetching user photo:', error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    /**
     * Clear photo cache
     */
    public clearPhotoCache(): void {
        this.photoCache.clear();
        ScimPhotoService.globalPhotoCache.clear();
        debugLog('🗑️  Cleared photo cache (instance and global)');
    }

    /**
     * Clear photo cache for a specific user
     */
    public clearPhotoCacheForUser(userId: string): void {
        if (this.photoCache.has(userId)) {
            this.photoCache.delete(userId);
        }
        if (ScimPhotoService.globalPhotoCache.has(userId)) {
            ScimPhotoService.globalPhotoCache.delete(userId);
        }
        debugLog(`🗑️  Cleared photo cache for user: ${userId}`);
    }

    /**
     * Check if photo is cached for a user
     */
    public isPhotoCached(userId: string): boolean {
        return this.photoCache.has(userId) || ScimPhotoService.globalPhotoCache.has(userId);
    }

    /**
     * Get cached photo for a user (without making API call)
     */
    public getCachedPhoto(userId: string): PhotoResponse | undefined {
        if (this.photoCache.has(userId)) {
            return this.photoCache.get(userId);
        }
        if (ScimPhotoService.globalPhotoCache.has(userId)) {
            return ScimPhotoService.globalPhotoCache.get(userId);
        }
        return undefined;
    }

    /**
     * Manually store a photo in the cache under an arbitrary key (e.g. person ID).
     * Useful when photo data was loaded via a $ref path and needs to be accessible by person ID too.
     */
    public cachePhoto(key: string, photoData: string): void {
        const entry: PhotoResponse = { Photo: photoData };
        this.photoCache.set(key, entry);
        ScimPhotoService.globalPhotoCache.set(key, entry);
    }

    /**
     * Fetch photo data from Photo entity's $ref URL
     * @param photoRef - The Photo entity's $ref URL
     * @returns Promise with photo data or empty object if no photo found
     */
    async getPhotoFromRef(photoRef: string): Promise<PhotoResponse> {
        try {
            // Check cache first using the photoRef as the key
            if (this.photoCache.has(photoRef)) {
                debugVerboseLog(`📸 Photo cache hit for ref: ${photoRef}`);
                return this.photoCache.get(photoRef)!;
            }

            if (ScimPhotoService.globalPhotoCache.has(photoRef)) {
                debugVerboseLog(`📸 Global photo cache hit for ref: ${photoRef}`);
                const photo = ScimPhotoService.globalPhotoCache.get(photoRef)!;
                this.photoCache.set(photoRef, photo); // Add to instance cache
                return photo;
            }

            debugVerboseLog(`🌐 ScimPhotoService: Fetching photo from ref: ${photoRef}`);

            const response = await this.api.get(photoRef);
            const photoEntity = response.data;
            debugVerboseLog(`📊 ScimPhotoService: Received photo entity response`, photoEntity);

            // Extract the Data field and create PhotoResponse format
            const photoData: PhotoResponse = {
                Photo: photoEntity.Data || null
            };

            // Cache the photo data
            this.photoCache.set(photoRef, photoData);
            ScimPhotoService.globalPhotoCache.set(photoRef, photoData);

            debugVerboseLog(`💾 Photo cached for ref: ${photoRef}`);
            return photoData;
        } catch (error) {
            console.error('❌ Error fetching photo from ref:', error);
            throw ScimAuthService.handleAuthError(error);
        }
    }
}
